import type { CrudColumnDef } from "@/components/tables/crudColumnDef";

export type ModalMode = "create" | "edit" | (string & {}) | undefined;

export interface BuildFormColumnsInput {
  columns?: CrudColumnDef[];
  additionalColumnsForCreate?: CrudColumnDef[] | Record<string, CrudColumnDef>;
  additionalColumnsForEdit?: CrudColumnDef[] | Record<string, CrudColumnDef>;
  modalMode: ModalMode;
}

const asList = (
  columns?: CrudColumnDef[] | Record<string, CrudColumnDef>
): CrudColumnDef[] => (columns ? Object.values(columns) : []);

export const buildFormColumns = ({
  columns,
  additionalColumnsForCreate,
  additionalColumnsForEdit,
  modalMode,
}: BuildFormColumnsInput): CrudColumnDef[] => {
  const formColumns = asList(columns).filter(
    (column) => column.enableForCreate
  );

  if (modalMode !== "edit") {
    for (const column of asList(additionalColumnsForCreate)) {
      if (column.columnOnTop) {
        formColumns.unshift(column);
      } else {
        formColumns.push(column);
      }
    }
  }

  if (modalMode === "edit") {
    formColumns.push(...asList(additionalColumnsForEdit));
  }

  return formColumns;
};

const presetObjectHasId = (column: CrudColumnDef, rawValue: any) =>
  rawValue?.id != null ||
  (column.userEntityValueField === "subject_id" &&
    rawValue?.subject_id != null);

const entityPresetValue = (column: CrudColumnDef, rawValue: any) => {
  if (column.asyncEntitySearch) return rawValue;
  if (column.userEntityValueField === "subject_id") {
    return rawValue.subject_id ?? rawValue.id;
  }
  return rawValue.id;
};

const emptyJsonBuilderValue = (column: CrudColumnDef) => {
  const jsonBuilderProps = column.jsonBuilderProps || {};
  if (jsonBuilderProps.isArrayOfObjects !== true) return {};
  const emptyArrayOfObjectsValue =
    jsonBuilderProps.emptyArrayOfObjectsValue || "emptyArray";
  return emptyArrayOfObjectsValue === "singleEmptyObject" ? [{}] : [];
};

const startValueForEmptyField = (column: CrudColumnDef) => {
  if (column.allowZeroAsValue) return 0;
  if (column.asyncEntity) return [];
  if (column.createAble) return "";
  return undefined;
};

export const buildInitialFormValues = (
  formColumns: CrudColumnDef[],
  presetValues: Record<string, any>,
  modalMode: ModalMode
): Record<string, any> => {
  const values: Record<string, any> = {};

  for (const column of formColumns) {
    const columnName = column.id as string;
    const rawValue = presetValues[columnName];

    values[columnName] =
      column.entity &&
      typeof rawValue === "object" &&
      presetObjectHasId(column, rawValue)
        ? entityPresetValue(column, rawValue)
        : rawValue;

    if (
      modalMode !== "edit" &&
      values[columnName] == null &&
      column.createDefaultValue !== undefined
    ) {
      values[columnName] = column.createDefaultValue;
    }

    if (column.useJsonBuilder === true && values[columnName] == null) {
      values[columnName] = emptyJsonBuilderValue(column);
    }

    const startValue = startValueForEmptyField(column);
    if (values[columnName] == null && startValue !== undefined) {
      values[columnName] = startValue;
    }
  }

  return values;
};
